<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'

import type { ImageFile, LoyaltyReward, UserMerchant } from '@/types/types'
import {
  isVisibleHomeBlock,
  isContentVisible,
  type BrandAppMissionFocus,
  type BrandAppNews,
  type BrandAppOfferCard,
  type BrandAppPreviewFocus,
  type HomeBlockId
} from '../lib/brandAppContract'
import type { BrandAppTabId } from '../lib/brandAppContract'
import { pathForZone, type PreviewZone } from '../composables/webappPageTabs'
import { COMING_SOON_SCREENS } from '../lib/brandAppSections'
import useBrandAppConfig from '../composables/useBrandAppConfig'
import useContentEditing from '../composables/useContentEditing'
import { EDITOR_LOYALTY_DATA } from '../composables/editorLoyaltyData'
import { isMissionShown, missionKey } from '../lib/brandAppMissions'
import { safeHttpUrl } from '../lib/brandAppUrl'
import { previewFont } from '../lib/brandAppFonts'
import { previewSocials } from '../lib/brandAppHomeApi'
import { emptyBlockImage, emptyNews, emptyOffers } from '../lib/brandAppPlaceholders'
import BrandAppPreviewFrame from './BrandAppPreviewFrame.vue'

const props = withDefaults(
  defineProps<{
    baseUrl: string
    cguUrl?: string
    cguWording?: string
    checkinPoints?: number
    hasFormCompletion?: boolean
    images: ImageFile[]
    logo: string
    merchant: UserMerchant
    pointsLabel?: string
    primaryColor?: string
    privacyPolicyUrl?: string
    registrationTexts?: Record<string, string>
    rewards?: LoyaltyReward[]
    slug?: string
    storeName: string
    subtitle?: string
    title: string
    previewZone: PreviewZone
    homeSection?: string | null
    isColorPreviewed?: boolean
    usePoliteLanguage?: boolean
    hasPointsByCurrency?: boolean
    previewStatusName?: string
    loyaltyPointsByCurrencyUnit?: string
  }>(),
  {
    images: () => [],
    primaryColor: '#000000'
  }
)

const iframeRef = ref<HTMLIFrameElement>()
const { completionFields, home, identity, loyaltyBlocks, order, socials, tabs } = useBrandAppConfig()
const { hasWallet } = useLocalStorage()

const previewOrigin = computed(() => {
  try {
    return new URL(props.baseUrl, window.location.origin).origin
  } catch {
    return window.location.origin
  }
})

const { locale, t } = useI18n()
const { draft: contentDraft, view: contentView } = useContentEditing()

const editedOffers = computed<BrandAppOfferCard[]>(() => {
  const cards = home.value.offerIds.flatMap((id) => {
    const card = home.value.offerCards[id]
    return card ? [{ ...card }] : []
  })
  const draft = contentDraft.value?.kind === 'offer' ? contentDraft.value.item : null
  if (!draft) return cards.filter(isContentVisible)
  const editedCard = {
    ...draft,
    title: draft.title.trim() || t('pages.loyaltySupports.webapp.offers.titlePlaceholder'),
    description: draft.description.trim() || t('pages.loyaltySupports.webapp.offers.descriptionPlaceholder'),
    ctaLabel: draft.ctaLabel.trim() || t('pages.loyaltySupports.webapp.offers.defaultButton')
  }
  const index = cards.findIndex((card) => card.offerId === editedCard.offerId)

  const merged = index === -1 ? [...cards, editedCard] : cards.map((card, i) => (i === index ? editedCard : card))

  return merged.filter((card) => card === editedCard || isContentVisible(card))
})

const editedActus = computed<BrandAppNews[]>(() => {
  const actus = home.value.actus
  const draft = contentDraft.value?.kind === 'actu' ? contentDraft.value.item : null
  if (!draft) return actus.filter(isContentVisible)
  const editedNews = {
    ...draft,
    title: draft.title.trim() || t('pages.loyaltySupports.webapp.editor.preview.newsTitle'),
    description: draft.description.trim() || t('pages.loyaltySupports.webapp.editor.preview.newsDescription'),
    ctaLabel: draft.ctaLabel.trim() || t('pages.loyaltySupports.webapp.news.button')
  }
  const index = actus.findIndex((news) => news.id === editedNews.id)

  const merged = index === -1 ? [...actus, editedNews] : actus.map((news, i) => (i === index ? editedNews : news))

  return merged.filter((news) => news === editedNews || isContentVisible(news))
})

const previewOffers = computed<BrandAppOfferCard[]>(() =>
  editedOffers.value.length
    ? editedOffers.value
    : emptyOffers({
        title: t('pages.loyaltySupports.webapp.editor.preview.emptyOffers'),
        hint: t('pages.loyaltySupports.webapp.editor.preview.emptyOffersHint'),
        cta: t('pages.loyaltySupports.webapp.editor.preview.addOffer'),
        description: t('pages.loyaltySupports.webapp.offers.descriptionPlaceholder'),
        ctaLabel: t('pages.loyaltySupports.webapp.offers.defaultButton')
      })
)

const previewActus = computed<BrandAppNews[]>(() =>
  editedActus.value.length
    ? editedActus.value
    : emptyNews({
        title: t('pages.loyaltySupports.webapp.editor.preview.emptyNews'),
        hint: t('pages.loyaltySupports.webapp.editor.preview.emptyNewsHint'),
        cta: t('pages.loyaltySupports.webapp.editor.preview.addNews'),
        description: t('pages.loyaltySupports.webapp.editor.preview.newsDescription'),
        ctaLabel: t('pages.loyaltySupports.webapp.news.button')
      })
)

const catalogUrl = computed(() => safeHttpUrl(order.value.catalogUrl))

const previewHome = computed(() => ({
  heroImage:
    home.value.heroImage ||
    emptyBlockImage({
      kind: 'hero',
      title: t('pages.loyaltySupports.webapp.editor.preview.emptyHero'),
      hint: t('pages.loyaltySupports.webapp.editor.preview.emptyHeroHint'),
      cta: t('pages.loyaltySupports.webapp.editor.preview.addHero')
    }),
  heroPlaceholder: !home.value.heroImage,
  menuImage:
    home.value.menuImage ||
    emptyBlockImage({
      kind: 'menu',
      title: t('pages.loyaltySupports.webapp.editor.preview.emptyMenu'),
      hint: t('pages.loyaltySupports.webapp.editor.preview.emptyMenuHint'),
      cta: t('pages.loyaltySupports.webapp.editor.preview.addMenu')
    }),
  menuPlaceholder: !home.value.menuImage,
  menuStoreUrls: Object.entries(home.value.menuStoreUrls)
    .map(([storeId, url]) => ({ storeId: Number(storeId), url: safeHttpUrl(url) }))
    .filter((row) => row.url !== ''),
  blocks: home.value.blocks.filter(isVisibleHomeBlock),
  blockTitles: { ...home.value.blockTitles },
  socials: previewSocials(socials.value),
  socialsPlaceholder:
    previewSocials(socials.value).length === 0
      ? emptyBlockImage({
          kind: 'social',
          title: t('pages.loyaltySupports.webapp.editor.preview.emptySocial'),
          hint: t('pages.loyaltySupports.webapp.editor.preview.emptySocialHint'),
          cta: t('pages.loyaltySupports.webapp.editor.preview.addSocial')
        })
      : null,
  offers: previewOffers.value.map((card) => ({ ...card })),
  news: previewActus.value.map((item) => ({
    ...item,
    ctaTarget:
      item.ctaTarget.type === 'external'
        ? { ...item.ctaTarget, url: safeHttpUrl(item.ctaTarget.url) }
        : { ...item.ctaTarget }
  }))
}))

const loyaltyData = inject(EDITOR_LOYALTY_DATA, null)

const missionDraft = computed(() => (contentDraft.value?.kind === 'mission' ? contentDraft.value : null))

const previewMissions = computed(() => {
  if (!loyaltyData) return []
  const draft = missionDraft.value
  const entries = loyaltyData.missions.value.map((mission, index) => ({ key: missionKey(mission, index), mission }))
  const merged = draft
    ? entries.some((entry) => entry.key === draft.key)
      ? entries.map((entry) => (entry.key === draft.key ? { key: draft.key, mission: draft.item } : entry))
      : [...entries, { key: draft.key, mission: draft.item }]
    : entries

  return merged
    .filter((entry) => isMissionShown(entry.mission, hasWallet.value) || entry.key === draft?.key)
    .map(({ mission }) => ({
      id: mission.id,
      type: mission.type,
      title: mission.title,
      subtitle: mission.subtitle,
      ctaLabel: mission.ctaLabel,
      iconType: mission.iconType,
      iconValue: mission.iconValue,
      points: mission.points,
      action: { destinationUrl: mission.destinationUrl ?? undefined }
    }))
})

const missionFocus = computed<BrandAppMissionFocus | null>(() => {
  const draft = missionDraft.value?.item

  return draft ? { id: draft.id, type: draft.type } : null
})

const previewFocus = computed<BrandAppPreviewFocus | null>(() => {
  const draft = contentDraft.value
  if (!draft || draft.kind === 'mission') return null
  const id = draft.kind === 'offer' ? String(draft.item.offerId) : draft.item.id

  return { kind: draft.kind, id, view: contentView.value }
})

const colorSample = computed<BrandAppPreviewFocus | null>(() => {
  if (!props.isColorPreviewed) return null
  const news = editedActus.value[0]
  const offer = editedOffers.value[0] ?? (news ? null : previewOffers.value[0])
  if (offer) return { kind: 'offer', id: String(offer.offerId), view: 'detail' }

  return news ? { kind: 'actu', id: news.id, view: 'detail' } : null
})

const savedTabs = computed(() => tabs.value.filter((tab) => !COMING_SOON_SCREENS.includes(tab)))

const homeFocus = computed(() => {
  const item = previewFocus.value ?? colorSample.value

  return {
    block: props.homeSection ?? null,
    kind: item?.kind ?? null,
    id: item?.id ?? null,
    view: item?.view ?? null
  }
})

const previewFonts = computed(() => ({
  heading: previewFont(identity.value.fontHeading),
  body: previewFont(identity.value.fontBody)
}))

const emit = defineEmits<{
  (e: 'tab-clicked', tab: string): void
  (e: 'block-clicked', block: PreviewBlock): void
}>()

type PreviewBlock = HomeBlockId | 'hero'

const CLICKABLE_BLOCKS: PreviewBlock[] = ['hero', 'offers', 'news', 'menu', 'social']

const dataUrlCache = new Map<string, string>()

const toDataUrl = async (path: string): Promise<string | null> => {
  const cached = dataUrlCache.get(path)
  if (cached) return cached

  try {
    const blob = await (await fetch(path)).blob()
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
    dataUrlCache.set(path, dataUrl)

    return dataUrl
  } catch (error) {
    console.error(error)

    return null
  }
}

const processImages = (images: ImageFile[]): Promise<string[]> =>
  Promise.all(images.map(async ({ path }) => (path.startsWith('blob:') ? ((await toDataUrl(path)) ?? '') : path))).then(
    (paths) => paths.filter(Boolean)
  )

const processedImages = shallowRef<string[]>([])
const isReady = ref(false)

const sendPreviewData = () => {
  if (!isReady.value) return
  if (!iframeRef.value?.contentWindow) return

  const zone = props.previewZone
  const previewData = {
    type: 'PREVIEW_UPDATE',
    data: {
      cguUrl: props.cguUrl,
      cguWording: props.cguWording,
      checkinPoints: props.checkinPoints,
      hasFormCompletion: props.hasFormCompletion,
      completionFields: [...completionFields.value],
      loyaltyBlocks: [...loyaltyBlocks.value],
      hasPointsByCurrency: props.hasPointsByCurrency,
      home: JSON.parse(JSON.stringify(previewHome.value)),
      catalogUrl: catalogUrl.value,
      homeFocus: { ...homeFocus.value },
      tabs: [...savedTabs.value],
      images: [...processedImages.value],
      isLoggedIn: zone === 'loyalty' || zone === 'optin',
      locale: locale.value,
      logo: props.logo,
      loyaltyPointsByCurrencyUnit: props.loyaltyPointsByCurrencyUnit,
      merchant: JSON.stringify(props.merchant),
      missions: JSON.stringify(previewMissions.value),
      missionFocus: missionFocus.value ? { ...missionFocus.value } : null,
      openOptinSheet: zone === 'optin',
      openPhoneSheet: zone === 'phone',
      path: pathForZone(zone),
      pointsLabel: props.pointsLabel,
      primaryColor: props.primaryColor,
      buttonColor: identity.value.buttonColor ?? null,
      textColor: home.value.dressing.homeTextColor ?? null,
      fonts: JSON.parse(JSON.stringify(previewFonts.value)),
      privacyPolicyUrl: props.privacyPolicyUrl,
      registrationStep: zone === 'profileInfo' ? 'customer-info' : 'store-selection',
      rewards: JSON.stringify(props.rewards),
      slug: props.slug,
      statusName: props.previewStatusName,
      storeName: props.storeName,
      subtitle: props.subtitle,
      title: props.title,
      usePoliteLanguage: props.usePoliteLanguage,
      ...props.registrationTexts
    }
  }

  iframeRef.value.contentWindow.postMessage(previewData, previewOrigin.value)
}

const debouncedSendPreviewData = useDebounceFn(sendPreviewData, 100)

watch(
  () => props.images,
  async (images) => {
    processedImages.value = await processImages(images)
    debouncedSendPreviewData()
  },
  { immediate: true, deep: true }
)

watch(
  () => [
    props.primaryColor,
    props.title,
    props.subtitle,
    props.cguUrl,
    props.cguWording,
    props.privacyPolicyUrl,
    props.hasFormCompletion,
    props.registrationTexts,
    props.previewZone,
    props.previewStatusName,
    props.usePoliteLanguage,
    props.logo,
    previewMissions.value,
    missionFocus.value,
    previewHome.value,
    catalogUrl.value,
    identity.value.buttonColor,
    home.value.dressing.homeTextColor,
    previewFonts.value,
    homeFocus.value,
    savedTabs.value,
    completionFields.value,
    loyaltyBlocks.value
  ],
  () => {
    debouncedSendPreviewData()
  },
  { deep: true }
)

const handleMessage = (event: MessageEvent) => {
  if (event.source !== iframeRef.value?.contentWindow || event.origin !== previewOrigin.value) return

  if (event.data?.type === 'PREVIEW_READY') {
    isReady.value = true
    sendPreviewData()
    return
  }

  if (event.data?.type === 'BRAND_APP_PREVIEW_BLOCK') {
    const block = event.data.data?.block as PreviewBlock | undefined
    if (block && CLICKABLE_BLOCKS.includes(block)) emit('block-clicked', block)
    return
  }

  if (event.data?.type !== 'BRAND_APP_PREVIEW_NAVIGATION') return

  const activeTab = event.data.data?.activeTab as BrandAppTabId | undefined
  if (!activeTab || !savedTabs.value.includes(activeTab)) return

  emit('tab-clicked', activeTab)
}

onMounted(() => {
  window.addEventListener('message', handleMessage)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handleMessage)
  dataUrlCache.clear()
})
</script>

<template>
  <div class="flex h-full min-h-0 items-start justify-center px-6">
    <BrandAppPreviewFrame class="h-[85%]">
      <iframe
        ref="iframeRef"
        :src="baseUrl"
        class="h-full w-full"
        :title="t('pages.loyaltySupports.webapp.editor.preview.frameTitle')"
      />
    </BrandAppPreviewFrame>
  </div>
</template>
